import { useCallback, RefObject } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

interface PrintOptions {
  content: () => HTMLElement | null;
  documentTitle?: string;
  onBeforePrint?: () => void;
  onAfterPrint?: () => void;
  filename?: string;
}

/**
 * Custom hook for generating PDF from React components
 * This uses html2canvas and jsPDF for better PDF generation
 */
export const useReactToPrint = (options: PrintOptions) => {
  const { 
    content, 
    documentTitle = 'ROI Construction - Marketing Report', 
    onBeforePrint, 
    onAfterPrint,
    filename = 'marketing-report.pdf'
  } = options;
  
  const handlePrint = useCallback(async () => {
    try {
      // Call the before print callback if provided
      if (onBeforePrint) {
        onBeforePrint();
      }
      
      // Get the element to convert to PDF
      const contentEl = content();
      if (!contentEl) {
        console.error('Content to print was not found!');
        return;
      }
      
      // Create a new jsPDF instance
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });
      
      // Set the title
      doc.setProperties({ title: documentTitle });
      
      // Convert the HTML element to canvas
      const canvas = await html2canvas(contentEl, {
        scale: 2, // Higher scale for better quality
        useCORS: true, // To handle images from different origins
        logging: false,
        backgroundColor: '#ffffff',
      });
      
      // Calculate dimensions to fit the page
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 210; // A4 width in mm (210mm)
      const pageHeight = 297; // A4 height in mm (297mm)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add image to the PDF (first page)
      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Add new pages if the content is higher than one page
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Save the PDF
      doc.save(filename);
      
      // Call the after print callback if provided
      if (onAfterPrint) {
        onAfterPrint();
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  }, [content, documentTitle, filename, onBeforePrint, onAfterPrint]);
  
  return handlePrint;
};

/**
 * Simple print hook for components using refs
 */
export const usePrintHook = () => {
  const printRef = useCallback((ref: RefObject<HTMLElement>) => {
    if (ref.current) {
      const printContents = ref.current.innerHTML;
      const originalContents = document.body.innerHTML;
      
      document.body.innerHTML = printContents;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    }
  }, []);

  return { printRef };
};

export default useReactToPrint;